import * as React from 'react'
import { useContext, useLayoutEffect, useRef } from 'react'

import {
  Interceptor,
  StreamResponse,
  Transport,
  UnaryResponse,
} from '@connectrpc/connect'
import { TransportProvider } from '@connectrpc/connect-query'
import { createConnectTransport } from '@connectrpc/connect-web'
import { datadogLogs } from '@datadog/browser-logs'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import { useAuth } from '@enechain/auth-js'
import { StreamTransportProvider } from '@enechain/connect-stream-query'
import { queryClient } from '@enechain/eposition-libs'

import { buildPreviewUrl } from '../utils/preview'
import { ApiHeadersContext } from './ApiHeadersProvider'

type ApiClientProviderProps = {
  baseUrl: string
  frontendBaseHost?: string
  children: (transport: Transport, queryClient: QueryClient) => React.ReactNode
}

const ApiClientProvider: React.FC<ApiClientProviderProps> = ({
  baseUrl: rawBaseUrl,
  frontendBaseHost,
  children,
}) => {
  const baseUrl = React.useMemo(
    () => buildPreviewUrl(rawBaseUrl, frontendBaseHost),
    [rawBaseUrl, frontendBaseHost],
  )
  const { user, getToken } = useAuth()

  const getTokenRef = useRef(getToken)
  // ページロード時に同じ API が複数回発火し最初のリクエストが abort されて backend に
  // context canceled が伝搬する問題への対応。getToken を deps に含めると毎レンダーで
  // authInterceptor → transport が再生成されるため、"latest ref" パターンで回避する。
  // deps を意図的に省略して毎レンダー実行し、ref を最新の getToken に同期させる。
  // https://github.com/enechain/eposition-app/pull/5396
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    getTokenRef.current = getToken
  })

  const authInterceptor = React.useCallback<Interceptor>(
    (next) =>
      async (req): Promise<UnaryResponse | StreamResponse> => {
        const token = await getTokenRef.current().catch((error: unknown) =>
          // eslint-disable-next-line no-console
          console.info(error),
        )
        if (token != null && token.length > 0) {
          req.header.set('Authorization', `bearer ${token}`)
        }
        return next(req)
      },
    // getTokenRef は安定した ref オブジェクトのため deps [] で問題ない。
    // .current は上の useLayoutEffect で毎レンダー更新される。
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const traceIdInterceptor = React.useCallback<Interceptor>(
    (next) =>
      async (req): Promise<UnaryResponse | StreamResponse> => {
        req.header.set('X-TraceId', crypto.randomUUID())
        return next(req)
      },
    [],
  )

  const ctx = useContext(ApiHeadersContext)
  if (ctx == null) {
    throw new Error('ApiClientProvider must be used within ApiHeadersProvider')
  }
  const contextHeadersInterceptor: Interceptor = React.useMemo(
    () => (next) => async (req) => {
      const bag = ctx.headersRef.current
      for (const [k, v] of Object.entries(bag)) {
        if (v != null && v !== '') {
          req.header.set(k, String(v))
        }
      }
      return next(req)
    },
    [ctx.headersRef],
  )

  const transport = React.useMemo(
    () =>
      createConnectTransport({
        baseUrl,
        interceptors: [
          authInterceptor,
          traceIdInterceptor,
          contextHeadersInterceptor,
        ],
      }),
    [baseUrl, authInterceptor, traceIdInterceptor, contextHeadersInterceptor],
  )

  const streamTransport = React.useMemo(
    () =>
      createConnectTransport({
        baseUrl,
        interceptors: [authInterceptor, contextHeadersInterceptor],
      }),
    [baseUrl, authInterceptor, contextHeadersInterceptor],
  )

  const hasLoggedUserRef = useRef(false)
  React.useEffect(() => {
    if (user == null || hasLoggedUserRef.current) {
      return
    }
    hasLoggedUserRef.current = true
    datadogLogs.setGlobalContextProperty(
      'user_id',
      user.memberId ?? 'Anonymous',
    )
  }, [user])

  return (
    <TransportProvider transport={transport}>
      {/* @enechain/connect-stream-query は CJS ビルドのため、型上は
          @connectrpc/connect/dist/cjs の Transport を要求する。実体は同一なので
          コンポーネント側が期待する型へキャストする。 */}
      <StreamTransportProvider
        transport={
          streamTransport as unknown as React.ComponentProps<
            typeof StreamTransportProvider
          >['transport']
        }
      >
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools initialIsOpen={false} />
          {children(transport, queryClient)}
        </QueryClientProvider>
      </StreamTransportProvider>
    </TransportProvider>
  )
}

export default ApiClientProvider
